// Battle AI (GDD §8.3). A tactical layer scores Strategic Points by value + threat +
// distance and assigns each squad an objective (capture / defend / attack / retreat);
// squad micro is delegated to the order system (orders auto-take cover, auto-target,
// and auto-reinforce). A production planner grows the economy and army toward the slot
// cap with a faction-appropriate composition. The AI MUST competently attack points —
// so capturing objectives is weighted above sitting on safe ground.

import { dist, type Vec2 } from "../engine/vec";
import { applyAbility } from "./combat";
import {
  canBuildUnit,
  enqueueStructure,
  enqueueTier,
  enqueueUnit,
  enqueueUpgrade,
  fortifyPoint,
} from "./economy";
import type { AiAssignment, AiState, Faction, Squad } from "./types";
import { World } from "./world";

const RETREAT_MORALE = 0.28;
const REPLAN_INTERVAL = 1.8;
const BUILD_INTERVAL = 2.4;

const COMPOSITION: Record<Faction, string[]> = {
  legion: ["legion_line", "legion_heavy", "legion_line", "legion_marshal", "legion_assault", "legion_line", "legion_vehicle", "legion_heavy", "legion_assault"],
  kindled: ["kindled_zealot", "kindled_firebearer", "kindled_zealot", "kindled_pyrarch", "kindled_shock", "kindled_zealot", "kindled_vehicle", "kindled_firebearer", "kindled_shock"],
};

export function stepAI(world: World, dt: number): void {
  for (const f of ["legion", "kindled"] as Faction[]) {
    const army = world.armies[f];
    if (!army.isAI) continue;
    if (!army.ai) army.ai = freshAi(f);
    const ai = army.ai;

    ai.buildTimer -= dt;
    if (ai.buildTimer <= 0) {
      planBuild(world, f);
      ai.buildTimer = BUILD_INTERVAL;
    }

    ai.replanTimer -= dt;
    if (ai.replanTimer <= 0) {
      planTactics(world, f, ai);
      ai.replanTimer = REPLAN_INTERVAL;
    }

    useHero(world, f);
  }
}

function freshAi(f: Faction): AiState {
  return { replanTimer: 0, assignments: {}, buildTimer: 1, personality: f };
}

// ---- production ------------------------------------------------------------

function planBuild(world: World, f: Faction): void {
  const army = world.armies[f];
  if (army.queue.length >= 2) return;

  // 1) Power infrastructure early.
  if (army.usedGeneratorSlots < 2 && army.req > 100) {
    if (enqueueStructure(world, f, "generator")) return;
  }

  // 2) Tier up once a small force is fielded and economy allows.
  if (army.hqTier < 2 && world.slotsUsed(f) >= 5 && army.req > 220) {
    if (enqueueTier(world, f)) return;
  }

  // 3) Fill the army along the composition cycle (skip units we can't yet build).
  const comp = COMPOSITION[f];
  const start = world.tickCount % comp.length;
  for (let i = 0; i < comp.length; i++) {
    const id = comp[(start + i) % comp.length];
    if (canBuildUnit(world, f, id).ok) {
      if (enqueueUnit(world, f, id)) return;
    }
  }

  // 4) Spend spare requisition on upgrades for a frontline squad.
  if (army.req > 160) {
    for (const sq of world.aliveSquads(f)) {
      const up = sq.def.upgrades?.find((u) => !sq.upgrades.includes(u.id));
      if (up && enqueueUpgrade(world, f, sq.id, up.id)) return;
    }
  }

  // 5) Fortify a forward held point.
  if (army.req > 220) {
    world.points.forEach((p, idx) => {
      if (p.owner === f && !p.hasOutpost && dist(p.pos, world.map.hq[f]) > 500) fortifyPoint(world, f, idx);
    });
  }

  // 6) A base turret if flush and undefended.
  if (army.req > 200 && army.pwr > 60 && !world.structures.some((s) => s.faction === f && s.kind === "turret")) {
    const hq = world.hqOf(f);
    if (hq) enqueueStructure(world, f, "turret", { x: hq.pos.x + (f === "legion" ? 80 : -80), y: hq.pos.y + 90 });
  }
}

// ---- tactics ---------------------------------------------------------------

interface Objective {
  idx: number;
  score: number;
  capacity: number;
}

function planTactics(world: World, f: Faction, ai: AiState): void {
  const enemy = world.enemyOf(f);
  const squads = world.aliveSquads(f).filter((s) => !s.broken && !s.frenzy);
  const maxValue = Math.max(...world.points.map((p) => p.value));
  const homePos = world.map.hq[f];

  // --- objective selection with force CONCENTRATION ---
  // Spreading squads one-per-point gets them defeated in detail, so the number of active
  // objectives scales with army size: a small army masses on ONE point. Faction
  // personality (GDD §5.5/§8.3): Legion consolidates the near front (steep distance
  // penalty); the Kindled push hard and far (shallow penalty).
  const defensive = f === "legion";
  const distDiv = defensive ? 320 : 520;
  const scored = world.points.map((p, idx) => {
    const enemyNear = countModelsNear(world, enemy, p.pos, 300);
    let s: number;
    if (p.owner !== f) s = p.value + 6 + (p.owner === enemy ? 3 : 0) + (p.isPower ? 4 : 0);
    else s = enemyNear > 0 ? p.value + enemyNear * 1.4 : p.value * 0.25;
    if (p.value === maxValue) s += defensive ? 4 : 2; // Legion prizes the center to hold
    s -= dist(p.pos, homePos) / distDiv;
    return { idx, score: s, enemyNear, dHome: dist(p.pos, homePos) };
  });

  const homeCandidates = scored.filter((s) => world.points[s.idx].owner === f).sort((a, b) => a.dHome - b.dHome);
  const homeIdx = homeCandidates.length ? homeCandidates[0].idx : scored.slice().sort((a, b) => a.dHome - b.dHome)[0].idx;

  const n = squads.length;
  const objectives: Objective[] = [];
  // A small army stays home-side and expands outward as it grows — marching a 3-squad
  // force to the far center is suicide against the faster Kindled horde.
  const reach = n < 5 ? 720 : n < 8 ? 1150 : Infinity;

  if (defensive) {
    // Legion: GARRISON every held point (1 squad each, fight from cover) and contest
    // ONE forward point with the remaining battlegroup — never strip a held position.
    const owned = scored.filter((s) => world.points[s.idx].owner === f).sort((a, b) => a.dHome - b.dHome);
    let reserved = 0;
    for (const s of owned) {
      if (reserved >= n - 1 && owned.length > 1) break; // keep at least 1 squad to push
      objectives.push({ idx: s.idx, score: s.score + 7, capacity: 1 });
      reserved++;
    }
    const push = scored
      .filter((s) => world.points[s.idx].owner !== f && s.dHome <= reach)
      .sort((a, b) => a.dHome - b.dHome)[0];
    if (push) objectives.push({ idx: push.idx, score: push.score + 3, capacity: Math.max(1, n - reserved) });
    if (objectives.length === 0) {
      const near = scored.slice().sort((a, b) => a.dHome - b.dHome)[0];
      objectives.push({ idx: near.idx, score: near.score, capacity: n });
    }
  } else {
    // Kindled: concentrate force; a small army masses on one point, a big one on two.
    const activeCount = Math.max(1, Math.min(3, Math.floor(n / 2)));
    let pool = scored.filter((s) => s.dHome <= reach);
    if (pool.length === 0) pool = scored.slice();
    const ranked = pool.sort((a, b) => b.score - a.score).slice(0, activeCount);
    ranked.forEach((s, i) => {
      const capacity = i === 0 ? Math.ceil(n * 0.6) : Math.max(1, Math.floor((n * 0.4) / Math.max(1, activeCount - 1)));
      objectives.push({ idx: s.idx, score: s.score + (activeCount - i) * 1.5, capacity });
    });
    if (n >= 5 && !ranked.some((r) => r.idx === homeIdx)) objectives.push({ idx: homeIdx, score: scored[homeIdx].score, capacity: 1 });
  }

  // process most-forward squads first so the front line gets the push objective
  squads.sort((a, b) => dist(b.center, homePos) - dist(a.center, homePos));

  for (const sq of squads) {
    if (sq.morale < sq.maxMorale * RETREAT_MORALE && sq.def.role !== "vehicle") {
      assign(sq, { kind: "retreat", pointIndex: -1, targetSquad: -1, pos: nearestFriendlyRefuge(world, f, sq.center) }, ai);
      continue;
    }

    const aggressive = sq.def.meleeCharger || sq.def.role === "assault" || sq.def.role === "vehicle";
    const shooter = !aggressive && sq.def.role !== "heavy";

    // shooters kite an onrushing melee threat while firing
    if (shooter) {
      const threat = world.nearestEnemySquad(f, sq.center, 150);
      if (threat && (threat.def.meleeCharger || threat.def.role === "assault") && !threat.broken) {
        assign(sq, { kind: "kite", pointIndex: -1, targetSquad: -1, pos: { x: sq.center.x + (sq.center.x - threat.center.x) * 2, y: sq.center.y + (sq.center.y - threat.center.y) * 2 } }, ai);
        continue;
      }
    }

    // aggressive types smash a nearby enemy squad
    if (aggressive) {
      const target = world.nearestEnemySquad(f, sq.center, 360);
      if (target) {
        assign(sq, { kind: "attack", pointIndex: -1, targetSquad: target.id, pos: { ...target.center } }, ai);
        continue;
      }
    }

    // pick the best objective with remaining capacity (distance-weighted)
    let best: Objective | null = null;
    let bestVal = -Infinity;
    for (const o of objectives) {
      if (o.capacity <= 0) continue;
      const val = o.score - dist(sq.center, world.points[o.idx].pos) / 240;
      if (val > bestVal) {
        bestVal = val;
        best = o;
      }
    }
    if (!best) best = objectives[objectives.length - 1];
    best.capacity--;
    const p = world.points[best.idx];
    // shooters engage the point at range (defend/attack-move); chargers walk on to capture
    const kind = p.owner === f || shooter ? "defend" : "capture";
    assign(sq, { kind, pointIndex: best.idx, targetSquad: -1, pos: { ...p.pos } }, ai);
  }
}

function assign(sq: Squad, a: AiAssignment, ai: AiState): void {
  const prev = ai.assignments[sq.id];
  const same = prev && prev.kind === a.kind && prev.pointIndex === a.pointIndex && prev.targetSquad === a.targetSquad;
  ai.assignments[sq.id] = a;
  if (same && sq.order.type !== "stop") return; // don't thrash orders

  switch (a.kind) {
    case "retreat":
      sq.stance = "retreat";
      sq.order = { type: "move", pos: a.pos, facing: null, targetSquad: -1, targetPoint: -1 };
      break;
    case "kite":
      // keep firing while backpedaling (aggressive stance → models auto-fire on the move)
      sq.stance = "aggressive";
      sq.order = { type: "move", pos: a.pos, facing: null, targetSquad: -1, targetPoint: -1 };
      break;
    case "attack":
      sq.stance = "aggressive";
      sq.order = { type: "attack", pos: null, facing: null, targetSquad: a.targetSquad, targetPoint: -1 };
      break;
    case "capture":
      sq.stance = "aggressive";
      sq.order = { type: "capture", pos: a.pos, facing: null, targetSquad: -1, targetPoint: a.pointIndex };
      break;
    case "defend":
      sq.stance = "hold";
      sq.order = { type: "attackmove", pos: a.pos, facing: null, targetSquad: -1, targetPoint: a.pointIndex };
      break;
    default:
      break;
  }
}

function countModelsNear(world: World, f: Faction, pos: Vec2, r: number): number {
  let n = 0;
  const r2 = r * r;
  for (const sq of world.squads) {
    if (sq.dead || sq.faction !== f) continue;
    for (const m of sq.models) if (m.alive && (m.pos.x - pos.x) ** 2 + (m.pos.y - pos.y) ** 2 < r2) n++;
  }
  return n;
}

function nearestFriendlyRefuge(world: World, f: Faction, from: Vec2): Vec2 {
  let best: Vec2 = world.map.hq[f];
  let bd = dist(best, from);
  for (const p of world.points) {
    if (p.owner !== f) continue;
    const d = dist(p.pos, from);
    if (d < bd) {
      bd = d;
      best = p.pos;
    }
  }
  return { ...best };
}

// ---- hero abilities --------------------------------------------------------

function useHero(world: World, f: Faction): void {
  const army = world.armies[f];
  const hero = army.hero;
  if (!hero || hero.dead || !hero.def.abilities) return;

  for (const ab of hero.def.abilities) {
    if ((hero.abilityCd[ab.id] ?? 0) > 0) continue;
    if (ab.kind === "rally") {
      const broken = world.aliveSquads(f).some((s) => (s.broken || s.frenzy) && dist(s.center, hero.center) < (ab.radius ?? 260));
      if (broken) applyAbility(world, hero, ab, null);
    } else if (ab.kind === "artillery" || ab.kind === "immolate") {
      const cluster = biggestEnemyCluster(world, f, hero.center, 900, 3);
      if (cluster) applyAbility(world, hero, ab, cluster);
    } else if (ab.kind === "inspire" || ab.kind === "charge_buff") {
      const inCombat = world.nearestEnemySquad(f, hero.center, 320);
      if (inCombat) applyAbility(world, hero, ab, null);
    }
  }
}

function biggestEnemyCluster(world: World, f: Faction, from: Vec2, range: number, minModels: number): Vec2 | null {
  const enemy = world.enemyOf(f);
  let best: Squad | null = null;
  let bestCount = minModels - 1;
  for (const sq of world.squads) {
    if (sq.dead || sq.faction !== enemy) continue;
    if (dist(sq.center, from) > range) continue;
    const count = sq.models.filter((m) => m.alive).length;
    if (count > bestCount) {
      bestCount = count;
      best = sq;
    }
  }
  return best ? { ...best.center } : null;
}
